import { useState, type FormEvent } from "react";
import type { Project, ProjectInput } from "../api/types";
import { githubRepoFromUrl, slugify } from "../lib/format";

export function ProjectForm({
  initial,
  submitLabel,
  onSubmit,
  pending,
  error,
}: {
  initial?: Project;
  submitLabel: string;
  onSubmit: (p: ProjectInput) => void;
  pending: boolean;
  error: Error | null;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [slug, setSlug] = useState(initial?.slug ?? "");
  const [slugTouched, setSlugTouched] = useState(!!initial);
  const [repoUrl, setRepoUrl] = useState(initial?.repo_url ?? "");
  const [baseBranch, setBaseBranch] = useState(initial?.base_branch ?? "main");
  const [githubRepo, setGithubRepo] = useState(initial?.github_repo ?? "");
  const [ghTouched, setGhTouched] = useState(!!initial);
  const [autoMerge, setAutoMerge] = useState(initial?.auto_merge ?? false);
  const [maxEnvs, setMaxEnvs] = useState(String(initial?.max_concurrent_envs ?? 10));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit({
      name: name.trim(),
      slug: slug.trim(),
      repo_url: repoUrl.trim(),
      base_branch: baseBranch.trim(),
      github_repo: githubRepo.trim() || null,
      auto_merge: autoMerge,
      max_concurrent_envs: Number(maxEnvs),
    });
  };

  return (
    <form className="form-stack settings-form" onSubmit={submit}>
      <label className="field">
        <span className="field-label">Name</span>
        <input
          className="input"
          value={name}
          required
          onChange={(e) => {
            setName(e.target.value);
            if (!slugTouched) setSlug(slugify(e.target.value));
          }}
        />
      </label>
      <label className="field">
        <span className="field-label">Slug</span>
        <input
          className="input"
          value={slug}
          required
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value);
          }}
        />
        <span className="field-help muted">Used in branch names and URLs.</span>
      </label>
      <label className="field">
        <span className="field-label">Repository URL</span>
        <input
          className="input"
          value={repoUrl}
          required
          placeholder="https://github.com/org/repo.git"
          onChange={(e) => {
            setRepoUrl(e.target.value);
            if (!ghTouched) setGithubRepo(githubRepoFromUrl(e.target.value) ?? "");
          }}
        />
      </label>
      <div className="field-row">
        <label className="field">
          <span className="field-label">Base branch</span>
          <input className="input" value={baseBranch} required onChange={(e) => setBaseBranch(e.target.value)} />
        </label>
        <label className="field">
          <span className="field-label">GitHub repository</span>
          <input
            className="input"
            value={githubRepo}
            placeholder="org/repo (empty if not on GitHub)"
            onChange={(e) => {
              setGhTouched(true);
              setGithubRepo(e.target.value);
            }}
          />
        </label>
      </div>
      <div className="field-row">
        <label className="field">
          <span className="field-label">Max concurrent environments</span>
          <input
            className="input"
            type="number"
            min={0}
            value={maxEnvs}
            onChange={(e) => setMaxEnvs(e.target.value)}
          />
        </label>
        <label className="checkbox field-checkbox">
          <input type="checkbox" checked={autoMerge} onChange={(e) => setAutoMerge(e.target.checked)} />
          Enable GitHub auto-merge for task PRs
        </label>
      </div>
      {error && <div className="error-box">{error.message}</div>}
      <div className="row-actions row-actions-start">
        <button className="btn btn-primary" disabled={pending}>
          {pending ? "Saving…" : submitLabel}
        </button>
      </div>
    </form>
  );
}
